<script lang="ts">
export default {
  name: 'InternalBinderReviewPage',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ArrowLeft, FileWarning, Loader, SearchX } from '@lucide/vue';
import ExtractionReviewShell from '@/components/document/ExtractionReviewShell.vue';
import BinderReviewNav from '@/components/v2/binder_review/BinderReviewNav.vue';
import PolicyOverrideTargetSummary from '@/components/v2/binder_review/PolicyOverrideTargetSummary.vue';
import AppButton from '@/components/v2/AppButton.vue';
import AppModal from '@/components/v2/AppModal.vue';
import { useBinderReviewSequence, binderReviewRouteQuery } from '@/composables/useBinderReviewSequence';
import { useToast } from '@/composables/useToast';
import { aiService } from '@/services/AIService';
import type { AIPromptResultWithReviews, ExtractionTarget } from '@/types/ai';
import type { BinderReviewItem, BinderReviewQuery, BinderReviewSource } from '@/types/BinderReview';

// Reviewing a binder that already belongs to a policy: the same extraction surface the
// external scan and the AI results page use, with a summary of the policy it updates
// instead of a create-a-policy panel. Finishing the review lives in the field list, so
// this page never calls a completion endpoint of its own.
const route = useRoute();
const router = useRouter();
const toast = useToast();

const accountId = computed(() => String(route.params.id));
const promptResultId = computed(() => Number(route.params.promptResultId));

// The filters the list was showing when this result was opened. They narrow the sequence
// so Back/Next stays inside that set rather than jumping to a row the reviewer had
// filtered away, and they come back with the reviewer when they return to the list.
const filters = computed<BinderReviewQuery>(() => ({
  status: route.query.status ? String(route.query.status) : '',
  source: (route.query.source ? String(route.query.source) : '') as BinderReviewSource | '',
  search: route.query.search ? String(route.query.search) : '',
}));

const promptResult = ref<AIPromptResultWithReviews | null>(null);
const isLoading = ref(true);
const loadError = ref<string | null>(null);
const notFound = ref(false);
const unsavedCount = ref(0);

// Completing a review takes this result out of a list filtered to what needs reviewing,
// which is indistinguishable from a result that was never on this client. Without this,
// saving a review landed the reviewer on "isn't available on this client".
const reviewedHere = ref(false);
const showCompletionPrompt = ref(false);
const nextAfterReview = ref<BinderReviewItem | null>(null);
const remainingAfterReview = ref(0);
const reviewedPolicyNumber = ref('');

const {
  sequence,
  currentItem: item,
  currentIndex,
  prevItem,
  nextItem,
  position,
  routeFor,
  isLoading: isLoadingSequence,
  loadFailed: sequenceFailed,
  reload: reloadSequence,
} = useBinderReviewSequence(
  accountId,
  () => ({
    source: 'internal' as const,
    promptResultId: promptResultId.value,
  }),
  filters,
);

const listRoute = computed(() => ({
  name: 'account-binder-reviews',
  params: { id: accountId.value },
  query: binderReviewRouteQuery(filters.value),
}));

const policiesRoute = computed(() => ({ path: `/account/${accountId.value}/policy-detail` }));

const isProcessing = computed(() => item.value?.status === 'processing');
const extractionFailed = computed(() => item.value?.status === 'failed');

async function load() {
  if (isLoadingSequence.value) {
    isLoading.value = true;
    return;
  }

  loadError.value = null;
  notFound.value = false;
  promptResult.value = null;

  // Without the list there is no way to tell whether this result belongs to this client,
  // and guessing either way is wrong -- say the page could not load instead.
  if (sequenceFailed.value) {
    loadError.value = 'Could not load this client’s extraction results.';
    isLoading.value = false;
    return;
  }

  // The prompt-result endpoints are account-agnostic, so this list -- scoped server-side
  // to the account in the URL -- is what decides whether the result may be shown here at
  // all. A result belonging to another client is simply absent from it.
  if (!item.value) {
    // Unless it left because it was just reviewed here, in which case the page keeps
    // showing what was applied while the reviewer decides where to go next.
    if (!reviewedHere.value) {
      notFound.value = true;
    }
    isLoading.value = false;
    return;
  }

  if (isProcessing.value || extractionFailed.value) {
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  try {
    promptResult.value = await aiService.getPromptResultWithReviews(promptResultId.value);
  } catch (err) {
    loadError.value = (err as Error).message || 'Could not load the extraction';
  } finally {
    isLoading.value = false;
  }
}

// The second guard, and the only one that catches a result whose policy was resolved by
// the extracted policy number rather than the binder's own document link -- that lookup
// is not account-scoped, so it can land on another client's policy.
function onTargetResolved(target: ExtractionTarget | null) {
  const belongsHere = target?.kind === 'policy_override' && Number(target.accountId) === Number(accountId.value);
  if (!belongsHere) {
    promptResult.value = null;
    notFound.value = true;
  }
}

// A reviewed binder drops out of a list filtered to what needs reviewing, so the sequence
// is reloaded and the next destination taken from the refreshed one. The reviewer is asked
// before moving: they have just applied a diff and may want to look at what they did.
async function onReviewCompleted() {
  unsavedCount.value = 0;
  reviewedHere.value = true;
  reviewedPolicyNumber.value = item.value?.policyNumber ?? '';

  const preferred = nextItem.value;
  await reloadSequence();

  const remaining = sequence.value.filter(
    (candidate) => !(candidate.source === 'internal' && candidate.promptResultId === promptResultId.value),
  );
  const next =
    remaining.find((candidate) => candidate.source === preferred?.source && candidate.promptResultId === preferred?.promptResultId) ??
    remaining[0] ??
    null;

  if (!next) {
    toast.success('Review saved and applied to the policy.');
    router.push(policiesRoute.value);
    return;
  }

  nextAfterReview.value = next;
  remainingAfterReview.value = remaining.length;
  showCompletionPrompt.value = true;
}

function goToNextAfterReview() {
  showCompletionPrompt.value = false;
  if (nextAfterReview.value) {
    router.push(routeFor(nextAfterReview.value));
  }
}

function goToPolicies() {
  showCompletionPrompt.value = false;
  router.push(policiesRoute.value);
}

function goToList() {
  router.push(listRoute.value);
}

function go(target: typeof prevItem.value) {
  if (target) {
    router.push(routeFor(target));
  }
}

// Registered before the loader so a step onto another result clears the completion state
// before that result is fetched.
watch(promptResultId, () => {
  reviewedHere.value = false;
  showCompletionPrompt.value = false;
  nextAfterReview.value = null;
});

watch([promptResultId, isLoadingSequence], load, { immediate: true });
</script>

<template>
  <div class="flex h-full min-h-0 flex-col gap-3 p-4">
    <div class="flex shrink-0 items-center gap-3">
      <AppButton
        data-testid="back-to-policies"
        @click="goToList">
        <ArrowLeft :size="16" /> Binder Scans
      </AppButton>
      <h1 class="text-xl font-semibold">Review Binder</h1>
      <span
        v-if="item?.documentName"
        class="truncate text-sm text-base-content/60">
        {{ item.documentName }}
      </span>

      <BinderReviewNav
        v-if="!notFound && currentIndex >= 0"
        class="ml-auto"
        :has-prev="!!prevItem"
        :has-next="!!nextItem"
        :position="position"
        :unsaved-count="unsavedCount"
        @prev="go(prevItem)"
        @next="go(nextItem)" />
    </div>

    <div
      v-if="isLoading"
      class="flex items-center gap-2 py-6"
      data-testid="binder-review-loading">
      <Loader
        :size="20"
        class="animate-spin text-secondary" />
      <span>Loading extraction…</span>
    </div>

    <div
      v-else-if="notFound"
      class="flex flex-col items-center gap-3 rounded-lg border border-base-300 p-10"
      data-testid="binder-review-not-found">
      <SearchX
        :size="32"
        class="text-base-content/60" />
      <p class="font-medium">This extraction isn't available on this client</p>
      <p class="text-sm text-base-content/60">It may have been deleted, or it belongs to a different account.</p>
      <AppButton @click="goToList">Back to binder scans</AppButton>
    </div>

    <div
      v-else-if="loadError"
      class="alert alert-error"
      data-testid="binder-review-error">
      <span>{{ loadError }}</span>
    </div>

    <div
      v-else-if="isProcessing"
      class="flex flex-col items-center gap-3 rounded-lg border border-base-300 p-10"
      data-testid="binder-review-processing">
      <Loader
        :size="32"
        class="animate-spin text-secondary" />
      <p class="font-medium">Scanning binder…</p>
      <p class="text-sm text-base-content/60">This can take a few minutes. You can leave this page and come back.</p>
    </div>

    <div
      v-else-if="extractionFailed"
      class="flex flex-col items-center gap-3 rounded-lg border border-error/40 p-10"
      data-testid="binder-review-failed">
      <FileWarning
        :size="32"
        class="text-error" />
      <p class="font-medium">We couldn't extract this binder</p>
      <p class="text-sm text-base-content/60">
        {{ item?.errorMessage || 'The scan finished without returning any policy details.' }}
      </p>
      <AppButton @click="goToList">Back to binder scans</AppButton>
    </div>

    <!-- Keyed on the result so stepping to another one remounts the viewer, the fields
         and the resolved target rather than leaving the previous binder's state behind. -->
    <ExtractionReviewShell
      v-else-if="promptResult"
      :key="promptResultId"
      :result="promptResult"
      @target-resolved="onTargetResolved"
      @pending-changes="(count) => (unsavedCount = count)"
      @review-completed="onReviewCompleted">
      <template #action-panel="{ target }">
        <PolicyOverrideTargetSummary
          v-if="target?.kind === 'policy_override'"
          :target="target"
          :reviews="promptResult?.reviews" />
      </template>
    </ExtractionReviewShell>

    <AppModal
      :show="showCompletionPrompt"
      title="Review saved"
      :handle-cancel="goToPolicies">
      <template #content>
        <div class="flex flex-col gap-2">
          <p>
            Applied to policy
            <span class="font-medium">{{ reviewedPolicyNumber || 'this binder' }}</span
            >.
          </p>
          <p class="text-sm text-base-content/70">
            {{ remainingAfterReview }} binder{{ remainingAfterReview === 1 ? '' : 's' }} still waiting on this client.
          </p>
        </div>
      </template>
      <template #actions>
        <button
          class="btn btn-sm"
          data-testid="review-saved-back"
          @click="goToPolicies">
          Back to policies
        </button>
        <button
          class="btn btn-sm btn-primary"
          data-testid="review-saved-next"
          @click="goToNextAfterReview">
          Next binder
        </button>
      </template>
    </AppModal>
  </div>
</template>
